import { service } from '@ember/service'
import Component from '@glimmer/component'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { themeKeys as statBoxThemes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import type { IntlService } from 'ember-intl'
import { StatBox } from 'teacher-ui-client/components/giraffe/stat-box'
import type { MentalMinuteSummaryData } from 'teacher-ui-client/components/stat-boxes/fluency-sprints'

interface Args {
  data: MentalMinuteSummaryData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardMentalMinute extends Component<Signature> {
  @service intl!: IntlService

  get additionSummary() {
    const sprintsMastered = this.args.data.summary.addition.sprints_mastered
    const improvement = this.args.data.summary.addition.improvement

    return {
      title: this.intl.t('reporting.charts.dashboard.mentalMinute.categories.addition'),
      category: 'addition',
      theme: statBoxThemes.THEME_OCEANY_BLUE,
      stats: [
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.sprintsMastered'),
            value: formatValue(sprintsMastered),
          },
        ],
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.improvement'),
            value: formatValue(improvement, { append: '%' }),
          },
        ],
      ],
    }
  }

  get subtractionSummary() {
    const sprintsMastered = this.args.data.summary.subtraction.sprints_mastered
    const improvement = this.args.data.summary.subtraction.improvement

    return {
      title: this.intl.t('reporting.charts.dashboard.mentalMinute.categories.subtraction'),
      category: 'subtraction',
      theme: statBoxThemes.THEME_JUICY_ORANGE,
      stats: [
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.sprintsMastered'),
            value: formatValue(sprintsMastered),
          },
        ],
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.improvement'),
            value: formatValue(improvement, { append: '%' }),
          },
        ],
      ],
    }
  }

  get multiplicationSummary() {
    const sprintsMastered = this.args.data.summary.multiplication.sprints_mastered
    const improvement = this.args.data.summary.multiplication.improvement

    return {
      title: this.intl.t('reporting.charts.dashboard.mentalMinute.categories.multiplication'),
      category: 'multiplication',
      theme: statBoxThemes.THEME_MS_GREEN,
      stats: [
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.sprintsMastered'),
            value: formatValue(sprintsMastered),
          },
        ],
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.improvement'),
            value: formatValue(improvement, { append: '%' }),
          },
        ],
      ],
    }
  }

  get divisionSummary() {
    const sprintsMastered = this.args.data.summary.division.sprints_mastered
    const improvement = this.args.data.summary.division.improvement

    return {
      title: this.intl.t('reporting.charts.dashboard.mentalMinute.categories.division'),
      category: 'division',
      theme: statBoxThemes.THEME_PURPLY_PINK,
      stats: [
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.sprintsMastered'),
            value: formatValue(sprintsMastered),
          },
        ],
        [
          {
            name: this.intl.t('reporting.charts.dashboard.mentalMinute.stats.improvement'),
            value: formatValue(improvement, { append: '%' }),
          },
        ],
      ],
    }
  }

  <template>
    <div data-test-mental-minute class="flex h-full flex-wrap items-stretch py-3 print:py-0" ...attributes>
      <StatBox
        data-test-stat-box={{this.additionSummary.category}}
        class="w-1/2 p-1 [&_.header]:basis-[30px] [&_.stat]:p-0 [&_.stats]:min-h-[calc(100%-30px)]"
        @title={{this.additionSummary.title}}
        @theme={{this.additionSummary.theme}}
        @stats={{this.additionSummary.stats}}
      />
      <StatBox
        data-test-stat-box={{this.subtractionSummary.category}}
        class="w-1/2 p-1 [&_.header]:basis-[30px] [&_.stat]:p-0 [&_.stats]:min-h-[calc(100%-30px)]"
        @title={{this.subtractionSummary.title}}
        @theme={{this.subtractionSummary.theme}}
        @stats={{this.subtractionSummary.stats}}
      />
      <StatBox
        data-test-stat-box={{this.multiplicationSummary.category}}
        class="w-1/2 p-1 [&_.header]:basis-[30px] [&_.stat]:p-0 [&_.stats]:min-h-[calc(100%-30px)]"
        @title={{this.multiplicationSummary.title}}
        @theme={{this.multiplicationSummary.theme}}
        @stats={{this.multiplicationSummary.stats}}
      />
      <StatBox
        data-test-stat-box={{this.divisionSummary.category}}
        class="w-1/2 p-1 [&_.header]:basis-[30px] [&_.stat]:p-0 [&_.stats]:min-h-[calc(100%-30px)]"
        @title={{this.divisionSummary.title}}
        @theme={{this.divisionSummary.theme}}
        @stats={{this.divisionSummary.stats}}
      />
    </div>
  </template>
}

export default DashboardMentalMinute
